import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const aiMockInterviews: FeatureContent = {
  slug: 'ai-mock-interviews',
  path: '/features/ai-mock-interviews',
  scorecardConnector:
    'The preparation signal in the candidate scorecard: how the candidate performed on the real role before the call.',
  audience: 'company',
  icon: 'Mic',
  name: 'Interview Practice',
  stage: 3,
  navLabel: 'AI Mock Interviews',
  navDescription: 'Candidates practise the actual role and arrive prepared.',
  primaryKeyword: 'AI mock interview',
  seo: {
    title: 'AI Mock Interview Practice | Talenture.ai',
    description:
      'AI mock interviews built from the actual job post, not generic questions. Candidates practise role-specific questions, get scored feedback, and arrive prepared.',
    keywords: [
      'AI mock interview',
      'AI interview practice',
      'practice interview questions',
      'mock interview tool',
      'role-specific interview prep',
      'AI interview simulator',
    ],
    ogTitle: 'AI Mock Interviews From the Real Job Post | Talenture.ai',
    ogDescription:
      'Candidates practise the actual interview, not generic questions. Role-specific questions and scored feedback per answer, so they arrive prepared.',
  },
  hero: {
    eyebrow: 'Stage 3 of the hiring mission · Prepare candidates',
    whereThisFits:
      'Once strong candidates surface, Talenture prepares them on the real role before they reach your calendar.',
    h1: 'AI Mock Interview Practice: Candidates Arrive Ready, Prepared for the Role',
    subhead:
      'Talenture builds a mock interview from your real job post. Candidates practise the questions that matter and get scored feedback, so the first real interview starts on the role, not on warm-up.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is an AI mock interview?',
    body: 'An AI mock interview is a practice session built from a specific role and candidate, not a generic question bank. Talenture generates tailored questions, transcribes spoken answers, and returns scored feedback with strengths and improvements, so candidates arrive prepared and interviewers get better signal from every conversation.',
  },
  sections: [
    {
      icon: 'ListChecks',
      title: 'Questions from the real role',
      body: 'Talenture generates a focused set of questions from the candidate profile and the job details, mixing types and ramping up difficulty so the practice mirrors the real thing.',
      bullets: [
        '5 tailored questions per session',
        'Technical, behavioural, situational, and company-specific',
        'Progressive difficulty, like a real interview',
      ],
    },
    {
      icon: 'AudioLines',
      title: 'Spoken answers, understood',
      body: 'Candidates answer out loud, the way they will on the day. Talenture transcribes each answer accurately so feedback is based on what was actually said.',
    },
    {
      icon: 'Gauge',
      title: 'Scored, actionable feedback',
      body: 'Every answer gets a clear assessment, not just a transcript. Candidates see what landed and what to tighten before the real conversation.',
      bullets: [
        'A 1 to 10 score per answer',
        'Specific strengths and improvements',
        'A concrete suggestion for each response',
      ],
    },
  ],
  visual: {
    component: 'MockInterviewSessionScreen',
    url: 'talenture.ai/mock-interview/senior-platform-engineer',
    caption: 'A practice session built from the real job post, scored answer by answer.',
  },
  supportingVisuals: [
    {
      component: 'MockInterviewFeedbackScreen',
      url: 'talenture.ai/mock-interview/senior-platform-engineer/results',
      heading: 'What comes back afterwards',
      caption:
        'A score out of ten per answer with the strengths and the fixes, and a consent card: nothing reaches the employer unless the candidate shares it.',
    },
  ],
  proofPoints: [
    { value: '5', label: 'tailored questions per session' },
    { value: '1 to 10', label: 'score on every answer' },
    { value: 'Role-specific', label: 'questions from the actual job post' },
  ],
  howTo: {
    heading: 'How an AI mock interview works',
    intro: 'Three steps from job post to a prepared candidate.',
    steps: [
      {
        title: 'Generate the session',
        body: 'Talenture reads the candidate profile and the job details and creates 5 tailored questions across technical, behavioural, and situational types.',
      },
      {
        title: 'Answer out loud',
        body: 'The candidate responds by voice. Talenture transcribes each answer so the feedback reflects exactly what was said.',
      },
      {
        title: 'Review the feedback',
        body: 'Each answer comes back with a score from 1 to 10, specific strengths, improvements, and a suggestion, so the candidate knows what to refine.',
      },
    ],
  },
  faq: [
    {
      question: 'Are the questions generic or role-specific?',
      answer:
        'Role-specific. Each session is built from the candidate profile and the actual job details, with a mix of technical, behavioural, situational, and company-specific questions at progressive difficulty. It is not a generic question bank.',
    },
    {
      question: 'How does the candidate answer?',
      answer:
        'Out loud, the way they will in the real interview. Talenture transcribes spoken answers accurately, so the feedback is based on what the candidate actually said rather than a typed summary.',
    },
    {
      question: 'What feedback does the candidate get?',
      answer:
        'Each answer receives a score from 1 to 10, a list of strengths, a list of improvements, and a concrete suggestion. The result is actionable preparation, not just a recording of the session.',
    },
    {
      question: 'How does this help the hiring team?',
      answer:
        'When candidates practise the real questions, they arrive prepared and the live interview starts on substance instead of warm-up. You get clearer signal from every conversation and waste less time on nerves.',
    },
  ],
  relatedSlugs: ['interview-scheduling', 'ai-interview-notes', 'match-insights'],
};
